<script setup lang="ts">
	import { AnalysisResult } from "@/types/scribe";
	import DOMPurify from "dompurify";
	import { ref, onMounted, onUnmounted, nextTick, getCurrentInstance } from "vue";
	import { isSmAndDown } from "@/helpers/display";
	import { markdownToHtml } from "@/api/scribe";
	import { useToast } from "vue-toastification";
	import ExcludedDocumentsNotice from "@/components/scribe/ExcludedDocumentsNotice.vue";
	import type { ExcludedDocument } from "@/components/common/entity/sparkyReportTypes";

	const props = defineProps<{
		analysis: AnalysisResult;
		excludedDocuments?: ExcludedDocument[];
	}>();

	const asHtml = ref("");
	const lightboxImageSrc = ref("");
	const lightboxOpen = ref(false);

	const openImageLightbox = (src: string) => {
		if (!src) return;
		lightboxImageSrc.value = src;
		lightboxOpen.value = true;
	};

	const closeImageLightbox = () => {
		lightboxOpen.value = false;
		lightboxImageSrc.value = "";
	};

	const handleKeydown = (e: KeyboardEvent) => {
		if (e.key === "Escape" && lightboxOpen.value) {
			e.preventDefault();
			e.stopImmediatePropagation();
			closeImageLightbox();
		}
	};

	onMounted(() => {
		document.addEventListener("keydown", handleKeydown);
	});

	onUnmounted(() => {
		document.removeEventListener("keydown", handleKeydown);
	});

	const instance = getCurrentInstance();
	const toast = useToast();
	const copyToClipboard = async (tableEl: Element) => {
		if (navigator.clipboard) {
			try {
				// Clone the table and strip action icons before copying
				const clone = tableEl.cloneNode(true) as HTMLElement;
				clone.querySelectorAll(".sean-table-actions").forEach((el) => el.remove());
				const html = clone.outerHTML;
				const plainText = document.createElement("div");
				plainText.innerHTML = html;
				const plainTextContent = plainText.textContent || plainText.innerText || "";
				await navigator.clipboard.write([
					new ClipboardItem({
						"text/html": new Blob([html], { type: "text/html" }),
						"text/plain": new Blob([plainTextContent], { type: "text/plain" })
					})
				]);
				toast.success("The table has been copied to your clipboard.", {
					zIndex: 10000
				});
			} catch (err) {
				toast.error("There was an error copying to the clipboard. Please copy the data manually.");
			}
		} else {
			toast.info("The clipboard is not available on this device. Please copy the data manually.");
		}
	};

	const downloadTableAsCsv = (tableEl: Element) => {
		const rows = tableEl.querySelectorAll("tr");
		const csvRows: string[] = [];
		rows.forEach((row) => {
			const cells = row.querySelectorAll("th, td");
			const csvCols: string[] = [];
			cells.forEach((cell) => {
				const img = cell.querySelector("img") as HTMLImageElement | null;
				let text = "";
				if (img) {
					text = img.currentSrc || img.src || "";
				} else {
					text = (cell as HTMLElement).innerText || cell.textContent || "";
				}
				// Escape CSV: wrap in quotes if it contains comma, quote, or newline
				text = text.replace(/\r?\n/g, " ").trim();
				if (/^[=+\-@]/.test(text)) text = "'" + text;
				if (text.includes(",") || text.includes('"') || text.includes("\n")) {
					text = '"' + text.replace(/"/g, '""') + '"';
				}
				csvCols.push(text);
			});
			csvRows.push(csvCols.join(","));
		});
		const csvString = csvRows.join("\n");
		const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "table.csv";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	onMounted(async () => {
		const html = await markdownToHtml(props.analysis.result);
		DOMPurify.addHook("afterSanitizeAttributes", (node) => {
			if (node.tagName === "A" && node.getAttribute("href")) {
				node.setAttribute("target", "_blank");
				node.setAttribute("rel", "noopener noreferrer");
			}
		});
		asHtml.value = DOMPurify.sanitize(html, { ADD_ATTR: ["target", "rel"] });
		DOMPurify.removeHook("afterSanitizeAttributes");

		// after the html is loaded, find all the tables, and add a "copy" icon in the top right corner of each table,
		// that shows up when the user hovers over the table.
		nextTick(() => {
			const tables = document.querySelectorAll(".scribe-analysis-viewer table");
			tables.forEach((table) => {
				// Wrapper for action icons (top-right corner of table)
				const actionsWrapper = document.createElement("span");
				actionsWrapper.className = "sean-table-actions";

				// Copy icon
				const copyIcon = document.createElement("span");
				copyIcon.className = "sean-table-action-btn";
				copyIcon.title = "Copy table";
				copyIcon.setAttribute("role", "button");
				copyIcon.innerHTML =
					'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19,21H8V7H19M19,5H8A2,2 0 0,0 6,7V21A2,2 0 0,0 8,23H19A2,2 0 0,0 21,21V7A2,2 0 0,0 19,5M16,1H4A2,2 0 0,0 2,3V17H4V3H16V1Z"/></svg>';
				copyIcon.addEventListener("click", async () => {
					await copyToClipboard(table);
				});
				actionsWrapper.appendChild(copyIcon);

				// Download CSV icon
				const downloadIcon = document.createElement("span");
				downloadIcon.className = "sean-table-action-btn";
				downloadIcon.title = "Download as CSV";
				downloadIcon.setAttribute("role", "button");
				downloadIcon.setAttribute("data-test-id", "report-table-csv");
				downloadIcon.innerHTML =
					'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/></svg>';
				downloadIcon.addEventListener("click", () => {
					downloadTableAsCsv(table);
				});
				actionsWrapper.appendChild(downloadIcon);

				table.appendChild(actionsWrapper);

				// Add an "expand" icon for images inside table cells (open image in a new tab/window)
				const images = table.querySelectorAll("img");
				images.forEach((img) => {
					const image = img as HTMLImageElement;
					if (image.dataset.seanExpandable === "true") {
						return;
					}

					const existingWrapper = image.parentElement?.classList.contains("sean-table-image-wrapper")
						? image.parentElement
						: null;
					const wrapper = existingWrapper || document.createElement("span");
					if (!existingWrapper) {
						wrapper.className = "sean-table-image-wrapper";
						image.parentNode?.insertBefore(wrapper, image);
						wrapper.appendChild(image);
					}

					const expandIcon = document.createElement("i");
					expandIcon.className = "icon-expand sean-image-expand";
					expandIcon.setAttribute("title", "Open image");
					expandIcon.setAttribute("aria-label", "Open image");
					expandIcon.setAttribute("role", "button");
					expandIcon.addEventListener("click", (event) => {
						event.preventDefault();
						event.stopPropagation();
						const src = image.currentSrc || image.src;
						if (!src) {
							return;
						}
						openImageLightbox(src);
					});

					// Make the thumbnail image itself clickable to open the lightbox
					image.style.cursor = "pointer";
					image.addEventListener("click", (event) => {
						event.preventDefault();
						event.stopPropagation();
						const src = image.currentSrc || image.src;
						if (!src) {
							return;
						}
						openImageLightbox(src);
					});

					// Ensure we don't add duplicate icons if this runs again
					if (!wrapper.querySelector(".sean-image-expand")) {
						wrapper.appendChild(expandIcon);
					}

					image.dataset.seanExpandable = "true";
				});

				// for desktop, we do a mouseover, for mobile we just show it all the time.
				if (isSmAndDown.value) {
					actionsWrapper.style.display = "flex";
				} else {
					actionsWrapper.style.display = "none";
					table.addEventListener("mouseenter", () => {
						actionsWrapper.style.display = "flex";
					});
					table.addEventListener("mouseleave", () => {
						actionsWrapper.style.display = "none";
					});
				}
			});

			// Make non-table images clickable to open in lightbox
			const allImages = document.querySelectorAll(".scribe-analysis-viewer img");
			allImages.forEach((img) => {
				const image = img as HTMLImageElement;
				// Skip images already handled inside tables
				if (image.dataset.seanExpandable === "true") {
					return;
				}
				image.style.cursor = "pointer";
				image.addEventListener("click", (event) => {
					event.preventDefault();
					event.stopPropagation();
					const src = image.currentSrc || image.src;
					if (!src) {
						return;
					}
					openImageLightbox(src);
				});
			});
		});
	});
</script>
<template>
	<div class="scribe-analysis-viewer" :class="{ 'is-mobile': isSmAndDown }">
		<ExcludedDocumentsNotice v-if="excludedDocuments?.length" :excluded-documents="excludedDocuments" />
		<div v-html="asHtml"></div>

		<!-- Image lightbox overlay -->
		<teleport to="body">
			<div v-if="lightboxOpen" class="sean-image-lightbox-overlay" @click.self="closeImageLightbox">
				<button class="sean-image-lightbox-close" aria-label="Close" @click="closeImageLightbox">
					<i class="icon-close-medium"></i>
				</button>
				<img :src="lightboxImageSrc" class="sean-image-lightbox-img" alt="Reference image" />
			</div>
		</teleport>
	</div>
</template>

<style>
	/* Interactive UI overlays — content styling comes from scribe-api analysis.css */

	/* LSP-16708: moving-inventory reports render wide multi-column tables. The report
	   dialog's v-card-text hides horizontal overflow, so when the window is resized
	   narrower the table columns were silently cut off. Let the report content scroll
	   horizontally within the dialog instead of clipping. Excludes the print clone
	   (printReport() builds it as "sean-print-clone scribe-analysis-viewer"): an
	   overflow-x: auto scroll container would also compute overflow-y to auto,
	   turning the clone into a single clipped scroll box and breaking multi-page
	   printing — the print media query keeps its own reduced-density layout. */
	.scribe-analysis-viewer:not(.sean-print-clone) {
		max-width: 100%;
		overflow-x: auto;
	}

	:is(.scribe-analysis-viewer, .sean-print-clone) img {
		max-width: 100%;
		height: auto;
	}

	:is(.scribe-analysis-viewer, .sean-print-clone) .sean-table-image-wrapper {
		position: relative;
		display: inline-block;
		max-width: 100%;
		line-height: 0;
	}

	:is(.scribe-analysis-viewer, .sean-print-clone) .sean-image-expand {
		position: absolute;
		top: 4px;
		right: 4px;
		cursor: pointer;
		color: #333;
		background-color: rgba(248, 249, 250, 0.95);
		padding: 4px;
		border-radius: 4px;
		font-size: 14px;
		opacity: 0;
		pointer-events: none;
		transition: opacity 0.12s ease-in-out;
	}

	:is(.scribe-analysis-viewer, .sean-print-clone) .sean-table-image-wrapper:hover .sean-image-expand {
		opacity: 1;
		pointer-events: auto;
	}

	:is(.scribe-analysis-viewer, .sean-print-clone).is-mobile .sean-image-expand {
		opacity: 1;
		pointer-events: auto;
	}

	:is(.scribe-analysis-viewer, .sean-print-clone) .sean-image-expand:hover {
		background-color: #e9ecef;
	}

	:is(.scribe-analysis-viewer, .sean-print-clone) .sean-table-actions {
		position: absolute;
		top: 4px;
		right: 4px;
		display: flex;
		gap: 4px;
		align-items: center;
		z-index: 1;
	}

	:is(.scribe-analysis-viewer, .sean-print-clone) .sean-table-action-btn {
		cursor: pointer;
		color: #333;
		background-color: #f8f9fa;
		padding: 4px;
		border-radius: 4px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		line-height: 0;
	}
	:is(.scribe-analysis-viewer, .sean-print-clone) .sean-table-action-btn:hover {
		background-color: #e9ecef;
	}

	/* Image lightbox overlay */
	.sean-image-lightbox-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background-color: rgba(0, 0, 0, 0.85);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 10000;
		padding: 40px;
	}

	.sean-image-lightbox-close {
		position: absolute;
		top: 16px;
		right: 16px;
		background: rgba(255, 255, 255, 0.9);
		border: none;
		border-radius: 50%;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		color: #333;
		font-size: 20px;
		z-index: 10001;
		transition: background-color 0.15s ease;
	}

	.sean-image-lightbox-close:hover {
		background-color: #fff;
	}

	.sean-image-lightbox-img {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
		border-radius: 8px;
		box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
	}
	/*
	 * Print layout — printReport() clones .scribe-analysis-viewer directly onto <body>
	 * as .sean-print-clone, then hides everything else. This sidesteps all of
	 * Vuetify's dialog positioning/height constraints entirely.
	 */
	/* Clone is only for printing — keep it invisible on screen */
	.sean-print-clone {
		display: none;
	}

	@media print {
		@page {
			margin: 12mm;
			size: auto;
		}

		/* Vuetify locks scroll by fixing html and hiding overflow when a dialog is open */
		html.v-overlay-scroll-blocked,
		body.printing-report {
			overflow: visible !important;
			height: auto !important;
			position: static !important;
		}

		body.printing-report > *:not(.sean-print-clone) {
			display: none !important;
		}

		.sean-print-clone {
			display: block !important;
		}

		/* Reduce table density so wide reports fit the page width */
		.sean-print-clone table {
			font-size: 0.75rem !important;
		}

		.sean-print-clone th,
		.sean-print-clone td {
			padding: 0.2rem 0.4rem !important;
			-webkit-print-color-adjust: exact !important;
			print-color-adjust: exact !important;
		}

		.sean-table-actions,
		.sean-image-expand {
			display: none !important;
		}
	}
</style>
